<template>
  <div class="row">
    <video-form
      v-model="videoMeta"
      :disabled="isUploading"
      @is-valid="e => formValid = e"
      @video-file-change="onVideoFileChange"
      @transcript-file-change="onTranscriptFileChange"
      ref="videoForm"
    ></video-form>
    <div class="col-md-12">
      <button class="submit-button btn btn-success save-button"
        :disabled="!validForm"
        @click="uploadVideo">
        <i :class="isUploading ? 'fa fa-spinner fa-spin' : 'fas fa-upload'" aria-hidden="true"></i>
        {{ isUploading ? 'Uploading...' : 'Upload Video' }}
      </button>
      <br/>
      <small v-if="isUploading">
        <i>This may take a few minutes. Please do not navigate away from this page.</i>
      </small>
    </div>
  </div>
</template>

<style scoped>
.row {
  display: flex;
  flex-wrap: wrap;
}
</style>

<script>
import TimeMixin from '@mixins/time-mixins';
import VideoForm from './video-form.vue';

export default {
  components: {
    VideoForm,
  },

  mixins: [TimeMixin],

  data() {
    return {
      videoMeta: {
        title: '',
        description: '',
        tags: [],
        dateRecorded: null,
      },
      videoFile: undefined,
      transcriptFile: undefined,
      isUploading: false,
      formValid: false,
    };
  },

  watch: {
    isUploading() {
      if (this.isUploading) {
        this.$Loading.minimal();
      } else {
        this.$Loading.finish();
      }
    },
  },

  computed: {
    validTranscript() {
      return !this.transcriptFile || (
        this.transcriptFile[0].type === 'text/vtt' && this.transcriptFile[0].name.endsWith('.vtt')
      );
    },

    validForm() {
      return Boolean(this.formValid && this.videoFile && this.videoMeta.dateRecorded && this.validTranscript);
    },
  },

  methods: {
    onVideoFileChange(file) {
      this.$logger.info('Staged video file for upload', { file: file.name });
      this.videoFile = file;
    },
    onTranscriptFileChange(file) {
      this.$logger.info('Staged transcript file for upload', { file: file.name });
      this.transcriptFile = file;
    },
    startProcessing(videoId) {
      this.$logger.info('Starting video procssing', { videoId }, true);
      return this.$http.post(`/api/curriculum/admin/video/${videoId}/processing/start`).then(() => {
        this.$logger.info('Successfully started video processing', { videoId });
      }).catch(err => {
        this.$logger.error('Error starting video processing', { videoId, file: this.videoFile.name }, err);
        throw err;
      });
    },
    updateVideoMeta(videoId) {
      const videoMeta = {
        video_file_name: this.videoFile[0].name,
        transcript_file_name: this.transcriptFile ? this.transcriptFile[0].name : null,
        name: this.videoMeta.title,
        description: this.videoMeta.description,
        tags: this.videoMeta.tags,
        date_recorded: this.formatDate(this.videoMeta.dateRecorded),
      };
      this.$logger.info('Updating video database entry with resource link', { videoMeta, file: this.videoFile.name }, true);
      return this.$http.put(`/api/curriculum/admin/video/${videoId}`, videoMeta).then(() => {
        this.$logger.info('Added video to the database successfully', { videoId });
        this.$ktoast.success('Video uploaded');
        this.clearFields();
        this.$emit('video-uploaded');
      }).catch(err => {
        this.$logger.error('Error updating video resource link', { videoMeta, file: this.videoFile.name }, err);
        throw err;
      });
    },
    uploadVideoToUrl(videoId, url) {
      this.$logger.info('Attempting video file upload', undefined, true);
      return this.$http.put(url, this.videoFile[0]).then(() => {
        this.$logger.info('Video file uploaded successfully', { videoId });
      }).catch(err => {
        this.$logger.error('Error uploading video with signed resumable upload URL', { videoId }, err);
        throw err;
      });
    },
    uploadTranscriptFile(videoId) {
      const file = this.transcriptFile[0];
      const formData = new FormData();
      formData.append('file', file);
      this.$logger.info('Uploading transcript file', { videoId, fileName: file.name }, true);
      return this.$http.post(`/api/curriculum/admin/video/${videoId}/upload-transcript`, formData)
        .then(() => {
          this.$logger.info('Transcript file uploaded successfully', { videoId });
        }).catch(err => {
          this.$logger.error('Error uploading transcript', { videoId }, err);
          throw err;
        });
    },
    getUploadVideoUrl(videoId) {
      const payload = {
        file_name: this.videoFile[0].name,
        file_size: this.videoFile[0].size,
        content_type: this.videoFile[0].type,
      };
      this.$logger.info('Getting signed resumable upload URL for video', { videoId });
      return this.$http.post(`/api/curriculum/admin/video/${videoId}/upload-video-url`, payload)
        .then(response => {
          this.$logger.info('Got signed resumable upload URL for video', { videoId });
          return response.data.upload_url;
        }).catch(err => {
          this.$logger.error('Error getting signed resumable upload URL  for video', undefined, err);
          throw err;
        });
    },
    addVideoToDB() {
      const videoMeta = {
        name: this.videoMeta.title,
        description: this.videoMeta.description,
        tags: this.videoMeta.tags,
        date_recorded: this.formatDate(this.videoMeta.dateRecorded),
      };
      this.$logger.info('Adding video to database', { videoMeta }, true);
      return this.$http.post('/api/curriculum/admin/video', videoMeta).then(res => {
        this.$logger.info('Added video to the database succesfully', { video_id: res.data.video_id });
        return res.data.video_id;
      }).catch(err => {
        this.$logger.error('Error writing video meta to database', { videoMeta }, err);
        throw err;
      });
    },
    async uploadVideo() {
      this.isUploading = true;
      try {
        const videoId = await this.addVideoToDB();
        const videoUrl = await this.getUploadVideoUrl(videoId);

        await this.uploadVideoToUrl(videoId, videoUrl);
        if (this.transcriptFile) {
          await this.uploadTranscriptFile(videoId);
        }
        await this.updateVideoMeta(videoId);
        await this.startProcessing(videoId);
      } catch (err) {
        this.$ktoast.showError(err);
      }
      this.isUploading = false;
    },

    clearFields() {
      this.$refs.videoForm.reset();
      this.videoFile = undefined;
      this.transcriptFile = undefined;
      this.videoMeta = {
        title: '',
        description: '',
        tags: [],
        dateRecorded: null,
      };
    },
  },
};
</script>
